import React, { useContext, useEffect, useState } from "react";
import { Form, Input, Select, Button, InputNumber, Modal } from "antd";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation } from "react-query";
import client from "src/api";
import { ExclamationCircleOutlined } from "@ant-design/icons";
import { APISomethingWrongMsg, NetworkDisconnectMsg } from "src/lib/constants";
import { getErrorMessage, logger } from "src/lib/utils";
import GlobalContext from "src/context/global-context";

const { Option } = Select;

const CardTypeForm = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;
  const { setIsAlertOpened, setAlertText } = useContext(GlobalContext);
  const [products, setProducts] = useState<any[]>([]);

  const [currencies, setCurrencies] = useState<any[]>([]);

  // GET CURRENCIES
  const { mutate: getCurrencies } = useMutation(client.currencies?.all, {
    onSuccess: (res: any) => {
      setCurrencies(res?.data || res || []);
    },
  });

  useEffect(() => {
    getCurrencies();
  }, []);

  // CREATE
  const { mutate: createCardType, isLoading: creating } = useMutation(
    client.cardTypes?.add,
    {
      onSuccess: (res: any) => {
        logger.log("ressss", res);
        setAlertText(res?.message || "Card design created successfully");
        setIsAlertOpened(true);
        navigate("/card-types");
      },
      onError: (error: any) => {
        if (error.code === "ERR_NETWORK") {
          setAlertText(NetworkDisconnectMsg);
        } else {
          if (error.response) {
            setAlertText(getErrorMessage(error.response.data));
          } else {
            setAlertText(APISomethingWrongMsg);
          }
        }
        setIsAlertOpened(true);
      },
    },
  );

  // UPDATE
  const { mutate: updateCardType, isLoading: updating } = useMutation(
    client.cardTypes?.update,
    {
      onSuccess: (res: any) => {
        setAlertText(res?.message || "Card design updated successfully");
        setIsAlertOpened(true);
        navigate("/card-types");
      },
      onError: (error: any) => {
        if (error.code === "ERR_NETWORK") {
          setAlertText(NetworkDisconnectMsg);
        } else {
          if (error.response) {
            setAlertText(getErrorMessage(error.response.data));
          } else {
            setAlertText(APISomethingWrongMsg);
          }
        }
        setIsAlertOpened(true);
      },
    },
  );

  const { mutate: getProducts } = useMutation(client.cardProducts.list, {
    onSuccess: (res: any) => {
      logger.log("Products", res);

      setProducts(res?.data?.data || []);
    },
  });

  useEffect(() => {
    getProducts({
      page: 1,
      limit: 100,
    });
  }, []);

  // GET DETAILS (EDIT)
  const { mutate: getDetails } = useMutation(client.cardTypes?.view, {
    onSuccess: (res: any) => {
      const data = res?.data;

      form.setFieldsValue({
        asset_id: data.asset_id,
        bank_type_id: data.bank_type_id,
        color_hex: data.color_hex,
        card_logo_name: data.card_logo_name,
        card_text_color: data.card_text_color,
        fee: data.fee,
        fee_type: data.fee_type,
        cash_out_limit: data.cash_out_limit,
        internal_transfers: data.internal_transfers,
        product_id: data.product_id,
      });
    },
  });

  useEffect(() => {
    if (isEdit && id) {
      getDetails({ id });
    }
  }, [id]);

  //  SUBMIT
  const onSubmit = (values: any) => {
    const selectedProduct = products.find(
      (item: any) => item.id === values.product_id,
    );

    const payload = {
      ...values,
      name: selectedProduct?.name || "",
      card_logo_name: values.card_logo_name,
      card_text_color: values.card_text_color,
    };

    Modal.confirm({
      icon: <ExclamationCircleOutlined />,
      title: isEdit ? "Confirm Update" : "Confirm Create",
      content: isEdit
        ? `Are you sure you want to update "${payload.name}"?`
        : "Are you sure you want to create this card design?",
      okText: "Yes",
      cancelText: "No",
      okButtonProps: {
        style: {
          backgroundColor: isEdit ? "#ef4444" : "#22c55e",
          borderColor: isEdit ? "#ef4444" : "#22c55e",
          color: "#fff",
        },
      },
      onOk: () => {
        if (isEdit) {
          updateCardType({
            id,
            ...payload,
          });
        } else {
          createCardType(payload);
        }
      },
    });
  };

  return (
    <div className="min-h-screen flex justify-center items-start pt-10 bg-gray-50">
      <div className="w-full max-w-[600px]">
        <h2 className="text-[24px] font-semibold mb-4">
          {isEdit ? "Edit" : "Add"} Card Design
        </h2>

        <div className="bg-white p-6 rounded-lg">
          <Form
            form={form}
            layout="vertical"
            onFinish={onSubmit}
            initialValues={{
              fee_type: "YEARLY",
              card_text_color: "#FFFFFF",
            }}
          >
            {/* Name */}
            <Form.Item
              label="Product"
              name="product_id"
              rules={[
                {
                  required: true,
                  message: "Please select product",
                },
              ]}
            >
              <Select placeholder="Select Product">
                {products.map((item: any) => (
                  <Option key={item.id} value={item.id}>
                    {item.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              name="bank_type_id"
              label="Card Type Id"
              rules={[{ required: true, message: "Card type ID is required" }]}
            >
              <Select size="large" placeholder="Card Type ID">
                <Option value={24}>24 - Aura</Option>
                <Option value={25}>25 - Vibe</Option>
              </Select>
            </Form.Item>

            <Form.Item
              name="card_logo_name"
              label="Card Logo Name"
              rules={[
                {
                  required: false,
                  message: "Please enter card logo name",
                },
                {
                  max: 255,
                  message: "Maximum 255 characters allowed",
                },
              ]}
            >
              <Input size="large" placeholder="e.g. FASTOO" />
            </Form.Item>

            <Form.Item
              name="card_text_color"
              label="Card Text Color"
              rules={[
                {
                  required: true,
                  message: "Please select card text color",
                },
              ]}
            >
              <input
                type="color"
                className="w-full h-[40px] cursor-pointer border rounded"
                value={form.getFieldValue("card_text_color") || "#FFFFFF"}
                onChange={(e) =>
                  form.setFieldsValue({
                    card_text_color: e.target.value,
                  })
                }
              />
            </Form.Item>

            {/* Color Picker */}
            <Form.Item
              name="color_hex"
              label="Card Color"
              rules={[{ required: true }]}
            >
              <input
                type="color"
                className="w-full h-[40px] cursor-pointer border rounded"
                value={form.getFieldValue("color_hex") || "#000000"}
                onChange={(e) =>
                  form.setFieldsValue({ color_hex: e.target.value })
                }
              />
            </Form.Item>

            {/* Submit */}
            <div className="flex gap-2 mt-4">
              <Button
                htmlType="submit"
                loading={creating || updating}
                className="bg-green-600 hover:bg-green-700 text-white border-none"
              >
                {isEdit ? "Update" : "Create"}
              </Button>

              <Button onClick={() => navigate("/card-types")}>Cancel</Button>
            </div>
          </Form>
        </div>
      </div>
    </div>
  );
};

export default CardTypeForm;
